import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Globe, Calendar, BookOpen, Loader } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePenNameProfile } from '../hooks/usePenNameProfile';
import BookIdeaCard from '../components/BookIdeaCard';
import SocialLinks from '../components/SocialLinks';

export default function PenNameProfile() {
  const { id = '' } = useParams();
  const { profile, loading, error } = usePenNameProfile(id);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error || 'Profile not found'}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.username}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-indigo-600" />
              </div>
            )}
            
            <div>
              {profile.full_name && (
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{profile.full_name}</h1>
              )}
              <p className="text-lg text-gray-600">@{profile.username}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="px-3 py-1 text-sm rounded-full bg-indigo-100 text-indigo-700">
                  Pen Name
                </span>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Writing since {formatDistanceToNow(new Date(profile.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {profile.bio && (
          <p className="text-gray-700 mb-4 whitespace-pre-wrap">{profile.bio}</p>
        )}

        {profile.website && (
          <a
            href={profile.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
          >
            <Globe className="w-4 h-4" />
            {profile.website}
          </a>
        )}

        {profile.social_links && Object.keys(profile.social_links).length > 0 && (
          <div className="mt-2">
            <SocialLinks links={profile.social_links} />
          </div>
        )}
      </div>

      {profile.book_ideas && profile.book_ideas.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Book Ideas
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profile.book_ideas.map((idea) => (
              <BookIdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}